'use client';
import React from 'react';
import Link from 'next/link';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { classNames } from '@repo/ui/src/utils/classNames';
import ProductPrice from '@src/components/Product/Price';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { useCart } from '@shopify/hydrogen-react';
import type { MeaterProduct } from '@src/domain/types/MeaterProduct';

interface AddOnProps {
  product: MeaterProduct;
  subtitle?: string;
}

const AddOn = ({ product, subtitle }: AddOnProps) => {
  const { t } = useTranslation();
  const { linesAdd } = useCart();
  const firstAvailableVariant = product.variants.find((v) => v.availableForSale);

  if (!firstAvailableVariant) return null;

  const onAdd = () => linesAdd([{ merchandiseId: firstAvailableVariant.id, quantity: 1 }]);

  const href = product.link?.link;

  return (
    <div
      className={classNames(
        'relative bg-white border border-neutral-200 flex gap-4 items-center pr-4',
        href && 'transition-colors [&:hover:not(:has(button:hover))]:border-neutral-400',
      )}
    >
      {href && <Link href={href} className="absolute inset-0" aria-label={product.title} />}
      <div className="overflow-clip w-[94px] h-[94px] flex items-center justify-center p-2">
        <img
          src={buildImageSrc(product.previewImage ?? product.images?.[0])}
          alt={product.previewImage?.accessibilityLabel || product.title}
          className="w-full h-full object-contain"
        />
      </div>

      <div className="flex flex-col flex-1">
        <p className="text-sm text-neutral-700 leading-5 font-normal">{product.title}</p>
        {subtitle && <p className="text-sm text-neutral-700 leading-5 font-normal">{subtitle}</p>}
        <ProductPrice
          product={product}
          variantId={firstAvailableVariant.id}
          variant="bodyM"
          className="text-neutral-700 font-bold"
          strikethroughClassName="text-neutral-500 font-normal"
        />
      </div>

      <button
        className={classNames(
          'relative z-10 border border-neutral-300 rounded-full shrink-0 w-12 h-12 flex items-center justify-center transition-colors',
          'hover:border-neutral-900',
        )}
        aria-label={t(
          'productDetailPageContent.addAddonLabel',
          { title: product.title },
          { default: `Add ${product.title}` },
        )}
        onClick={onAdd}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
          <path
            d="M12 5V19"
            stroke="#1a1a1a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5 12H19"
            stroke="#1a1a1a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default AddOn;
